import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { SupportChatService, SupportFaqItem } from '../../shared/services/support-chat.service';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-faq-settings',
  templateUrl: './faq-settings.component.html',
  styleUrls: ['./faq-settings.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, MatProgressSpinnerModule, BreadcrumbComponent]
})
export class FaqSettingsComponent implements OnInit {
  faqs: SupportFaqItem[] = [];
  isLoading: boolean = false;
  searchQuery: string = '';

  // Selection & Expansion
  selectedFaqIds: Set<number> = new Set<number>();
  expandedFaqIds: Set<number> = new Set<number>();

  // Modal / Form state
  showCreateModal: boolean = false;
  isEditing: boolean = false;
  isSaving: boolean = false;
  editingId: number | null = null;

  // Form model (Question, Answer, Display Order)
  faqForm = {
    question: '',
    answer: '',
    category: 'General',
    display_order: 1
  };

  // Delete modal state
  showDeleteModal: boolean = false;
  deletingFaq: SupportFaqItem | null = null;
  isDeleting: boolean = false;

  constructor(
    private supportChatService: SupportChatService,
    private cdr: ChangeDetectorRef,
    private toastr?: ToastrService
  ) {}

  ngOnInit(): void {
    this.loadFaqs();
  }

  loadFaqs(): void {
    this.isLoading = true;
    this.supportChatService.getFaqs().subscribe({
      next: (data) => {
        this.faqs = (data || []).sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load FAQs:', err);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    });
  }

  get filteredFaqs(): SupportFaqItem[] {
    let list = [...this.faqs];
    const q = this.searchQuery ? this.searchQuery.trim().toLowerCase() : '';
    if (q) {
      list = list.filter(f =>
        (f.question && f.question.toLowerCase().includes(q)) ||
        (f.answer && f.answer.toLowerCase().includes(q)) ||
        (f.id && String(f.id).includes(q))
      );
    }
    return list;
  }

  onSearchChange(): void {
    this.cdr.detectChanges();
  }

  isFaqSelected(faq: SupportFaqItem): boolean {
    return !!(faq.id && this.selectedFaqIds.has(faq.id));
  }

  toggleSelectFaq(faq: SupportFaqItem, event?: Event): void {
    if (event) event.stopPropagation();
    if (!faq.id) return;
    if (this.selectedFaqIds.has(faq.id)) {
      this.selectedFaqIds.delete(faq.id);
    } else {
      this.selectedFaqIds.add(faq.id);
    }
    this.cdr.detectChanges();
  }

  toggleExpand(faq: SupportFaqItem, event?: Event): void {
    if (event) event.stopPropagation();
    if (!faq.id) return;
    if (this.expandedFaqIds.has(faq.id)) {
      this.expandedFaqIds.delete(faq.id);
    } else {
      this.expandedFaqIds.add(faq.id);
    }
    this.cdr.detectChanges();
  }

  isExpanded(faq: SupportFaqItem): boolean {
    return !!(faq.id && this.expandedFaqIds.has(faq.id));
  }

  toggleFaqVisibility(faq: SupportFaqItem, event?: Event): void {
    if (event) event.stopPropagation();
    if (!faq || !faq.id) return;
    const newStatus = faq.is_active === false ? true : false;
    this.supportChatService.updateFaq(faq.id, {
      is_active: newStatus
    }).subscribe({
      next: () => {
        faq.is_active = newStatus;
        if (this.toastr) {
          this.toastr.success(`FAQ ${newStatus ? 'is now visible' : 'is now hidden'} in the support widget`);
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to toggle FAQ visibility:', err);
        if (this.toastr) this.toastr.error('Failed to update visibility');
      }
    });
  }

  getAvatarColor(faq: SupportFaqItem): string {
    const colors = ['#16a34a', '#2563eb', '#d97706', '#0891b2', '#7c3aed', '#0d9488'];
    const idx = (faq.id || faq.display_order || 1) % colors.length;
    return colors[idx];
  }

  openCreateModal(): void {
    this.isEditing = false;
    this.editingId = null;
    this.faqForm = {
      question: '',
      answer: '',
      category: 'General',
      display_order: this.faqs.length + 1
    };
    this.showCreateModal = true;
  }

  openEditModal(faq: SupportFaqItem, event?: Event): void {
    if (event) event.stopPropagation();
    this.isEditing = true;
    this.editingId = faq.id || null;
    this.faqForm = {
      question: faq.question || '',
      answer: faq.answer || '',
      category: faq.category || 'General',
      display_order: faq.display_order || 1
    };
    this.showCreateModal = true;
  }

  closeCreateModal(): void {
    this.showCreateModal = false;
    this.isEditing = false;
    this.editingId = null;
  }

  saveFaq(): void {
    if (!this.faqForm.question.trim() || !this.faqForm.answer.trim()) {
      if (this.toastr) {
        this.toastr.warning('Please enter both Header (Question) and Description (Answer).');
      } else {
        alert('Please enter both Header (Question) and Description (Answer).');
      }
      return;
    }

    this.isSaving = true;

    if (this.isEditing && this.editingId) {
      this.supportChatService.updateFaq(this.editingId, {
        question: this.faqForm.question.trim(),
        answer: this.faqForm.answer.trim(),
        category: 'General',
        display_order: Number(this.faqForm.display_order) || 1
      }).subscribe({
        next: () => {
          this.isSaving = false;
          this.closeCreateModal();
          this.loadFaqs();
          if (this.toastr) this.toastr.success('FAQ updated successfully');
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error updating FAQ:', err);
          if (this.toastr) this.toastr.error('Failed to update FAQ');
          this.cdr.detectChanges();
        }
      });
    } else {
      this.supportChatService.createFaq({
        question: this.faqForm.question.trim(),
        answer: this.faqForm.answer.trim(),
        category: 'General',
        display_order: Number(this.faqForm.display_order) || 1
      }).subscribe({
        next: () => {
          this.isSaving = false;
          this.closeCreateModal();
          this.loadFaqs();
          if (this.toastr) this.toastr.success('FAQ created successfully');
        },
        error: (err) => {
          this.isSaving = false;
          console.error('Error creating FAQ:', err);
          if (this.toastr) this.toastr.error('Failed to create FAQ');
          this.cdr.detectChanges();
        }
      });
    }
  }

  confirmDeleteFaq(faq: SupportFaqItem, event?: Event): void {
    if (event) event.stopPropagation();
    this.deletingFaq = faq;
    this.showDeleteModal = true;
  }

  closeDeleteModal(): void {
    this.showDeleteModal = false;
    this.deletingFaq = null;
  }

  executeDeleteFaq(): void {
    if (!this.deletingFaq || !this.deletingFaq.id) return;
    this.isDeleting = true;
    this.supportChatService.deleteFaq(this.deletingFaq.id).subscribe({
      next: () => {
        this.isDeleting = false;
        this.closeDeleteModal();
        this.loadFaqs();
        if (this.toastr) this.toastr.success('FAQ deleted successfully');
      },
      error: (err) => {
        this.isDeleting = false;
        console.error('Error deleting FAQ:', err);
        if (this.toastr) this.toastr.error('Failed to delete FAQ');
        this.cdr.detectChanges();
      }
    });
  }
}
